/**
 * User Service
 * Handles all user-related API calls including authentication and registration
 *
 * New Authentication Flow (LINE-based):
 * 1. Check if user is registered: POST /auth/line (returns tokens if registered, 401 if not)
 * 2. Initiate registration: POST /auth/line/register (sends SMS, returns registration_id)
 * 3. Verify registration: POST /auth/line/register/verify (verifies code, returns tokens)
 */

import { apiClient, HeaderType } from "@/lib/api-client";
import liff from "@line/liff";

// ============================================================================
// Type Definitions - Matching OpenAPI Spec
// ============================================================================

/**
 * User information from authentication response
 */
export interface UserInfo {
  id: string; // Internal user ID
  line_user_id: string; // LINE user ID (sub claim)
  name?: string; // Display name from LINE
  picture?: string; // Profile picture URL from LINE
  email?: string; // Email address from LINE
}

/**
 * Available role information
 */
export interface AvailableRole {
  role_id: string;
  role_type: string; // "customer" | "store" | "station" | "admin" | "bot"
  name: string;
  store_id?: number; // Present if role_type === "store"
  station_id?: number; // Present if role_type === "station"
}

/**
 * Authentication response (from POST /auth/line and POST /auth/line/register/verify)
 * Contains OIDC tokens and user information
 */
export interface AuthResponse {
  id_token: string; // OIDC ID token (JWT)
  access_token: string; // OAuth 2.0 access token (JWT)
  refresh_token: string; // Refresh token (session ID)
  token_type: "Bearer"; // Token type per OAuth 2.0 spec
  expires_in: number; // Access token expiration in seconds
  user: UserInfo;
  available_roles: AvailableRole[];
}

/**
 * Registration initiation response (from POST /auth/line/register)
 */
export interface RegistrationInitiateResponse {
  registration_id: string; // Registration session ID (use for verification)
  phone: string; // Masked phone number (e.g., "0912***678")
  expires_in: number; // Session TTL in seconds (180s = 3 minutes)
  channel_id: string; // LIFF channel ID from token
}

/**
 * Structured error response from backend
 */
export interface APIErrorResponse {
  error: {
    code: "VALIDATION_ERROR" | "AUTHENTICATION_ERROR" | "INTERNAL_ERROR";
    message: string;
    details?: {
      attempts_remaining?: number; // For 422 invalid verification code
      [key: string]: unknown;
    };
  };
}

/**
 * Generic API response wrapper for compatibility
 */
export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// ============================================================================
// Token Storage (simple in-memory storage for now)
// ============================================================================

let storedAuthTokens: AuthResponse | null = null;

/**
 * Store authentication tokens after successful login/registration
 */
function storeAuthTokens(tokens: AuthResponse): void {
  storedAuthTokens = tokens;
  // TODO: Consider persisting to localStorage/sessionStorage if needed
  // For now, keeping in memory means tokens expire on page reload (forces re-login)
}

/**
 * Get stored authentication tokens
 */
function getAuthTokens(): AuthResponse | null {
  return storedAuthTokens;
}

/**
 * Clear stored authentication tokens (logout)
 */
export function clearAuthTokens(): void {
  storedAuthTokens = null;
}

/**
 * Get the current backend access token
 */
export function getBackendAccessToken(): string | null {
  return storedAuthTokens?.access_token || null;
}

// ============================================================================
// User Service Class
// ============================================================================

/**
 * User Service
 * Encapsulates all user operations including authentication and registration
 */
export class UserService {
  /**
   * Check if user is registered and get authentication tokens
   * POST /auth/line
   *
   * @returns Promise with registration status and tokens if registered
   *
   * Success (200): User is registered, returns auth tokens
   * Error (401): User not registered (not an error, just means need to register)
   * Error (400): Validation error (invalid id_token)
   * Error (403): LINE channel not whitelisted
   */
  static async checkUserStatus(): Promise<{
    isRegistered: boolean;
    authData?: AuthResponse;
  }> {
    try {
      const idToken = liff.getIDToken();
      const accessToken = liff.getAccessToken();

      if (!idToken) {
        console.error("No LINE ID token available");
        return { isRegistered: false };
      }

      const response = await apiClient.post<AuthResponse>(
        "/auth/line",
        {
          id_token: idToken,
          access_token: accessToken,
          preferred_role_type: "customer",
        },
        {
          headerTypes: [HeaderType.JSON_CONTENT, HeaderType.NO_CACHE],
          skipAuthRedirect: true, // Don't auto-redirect on 401
        },
      );

      // Success - user is registered
      storeAuthTokens(response);

      return {
        isRegistered: true,
        authData: response,
      };
    } catch (error: unknown) {
      // 401 means user is not registered (expected case)
      if (error && typeof error === "object" && "statusCode" in error) {
        const apiError = error as { statusCode: number; message: string };

        if (apiError.statusCode === 401) {
          return { isRegistered: false };
        }
      }

      // Other errors are unexpected
      console.error("checkUserStatus error:", error);
      return { isRegistered: false };
    }
  }

  /**
   * Initiate registration by sending SMS verification code
   * POST /auth/line/register
   *
   * @param phone - User's phone number (e.g., "0912345678")
   * @returns Promise with registration session ID or error
   *
   * Success (200): SMS sent, returns registration_id
   * Error (400): Validation error (invalid phone or id_token)
   * Error (403): LINE channel not whitelisted
   * Error (409): Phone number or LINE account already registered
   * Error (429): Rate limit exceeded (too many attempts)
   */
  static async initiateRegistration(
    phone: string,
  ): Promise<ApiResponse<RegistrationInitiateResponse>> {
    try {
      const idToken = liff.getIDToken();
      const accessToken = liff.getAccessToken();

      if (!idToken) {
        return {
          success: false,
          error: "無法取得 LINE 認證資訊，請重新登入",
        };
      }

      const response = await apiClient.post<RegistrationInitiateResponse>(
        "/auth/line/register",
        {
          id_token: idToken,
          access_token: accessToken,
          phone,
          preferred_role_type: "customer",
        },
        {
          headerTypes: [HeaderType.JSON_CONTENT, HeaderType.NO_CACHE],
          skipAuthRedirect: true,
        },
      );

      return {
        success: true,
        data: response,
        message: "驗證碼已發送至您的手機",
      };
    } catch (error: unknown) {
      console.error("initiateRegistration error:", error);

      if (error && typeof error === "object" && "statusCode" in error) {
        const apiError = error as { statusCode: number; message: string };

        // Handle specific error codes
        switch (apiError.statusCode) {
          case 400:
            return {
              success: false,
              error: "手機號碼格式錯誤或無效",
            };
          case 403:
            return {
              success: false,
              error: "此 LINE 帳號無法使用此服務",
            };
          case 409:
            return {
              success: false,
              error: "此手機號碼或 LINE 帳號已被註冊",
            };
          case 429:
            return {
              success: false,
              error: "發送次數過多，請稍後再試",
            };
          default:
            return {
              success: false,
              error: apiError.message || "發送驗證碼失敗",
            };
        }
      }

      return {
        success: false,
        error: error instanceof Error ? error.message : "發送驗證碼失敗",
      };
    }
  }

  /**
   * Verify SMS code and complete registration
   * POST /auth/line/register/verify
   *
   * @param registrationId - Registration session ID from initiateRegistration
   * @param code - 6-digit verification code from SMS
   * @returns Promise with authentication tokens or error
   *
   * Success (201): Registration complete, returns auth tokens
   * Error (400): Validation error (invalid code format)
   * Error (404): Registration session not found
   * Error (410): Registration session expired
   * Error (422): Invalid verification code (includes attempts_remaining)
   */
  static async verifyRegistration(
    registrationId: string,
    code: string,
  ): Promise<ApiResponse<AuthResponse>> {
    try {
      const response = await apiClient.post<AuthResponse>(
        "/auth/line/register/verify",
        {
          registration_id: registrationId,
          verification_code: code,
        },
        {
          headerTypes: [HeaderType.JSON_CONTENT, HeaderType.NO_CACHE],
          skipAuthRedirect: true,
        },
      );

      // Success - store tokens and return
      storeAuthTokens(response);

      return {
        success: true,
        data: response,
        message: "註冊成功！",
      };
    } catch (error: unknown) {
      console.error("verifyRegistration error:", error);

      if (error && typeof error === "object" && "statusCode" in error) {
        const apiError = error as { statusCode: number; message: string };

        // Handle specific error codes
        switch (apiError.statusCode) {
          case 400:
            return {
              success: false,
              error: "驗證碼格式錯誤",
            };
          case 404:
            return {
              success: false,
              error: "註冊請求已失效，請重新開始",
            };
          case 410:
            return {
              success: false,
              error: "驗證碼已過期，請重新發送",
            };
          case 422: {
            // Try to extract attempts_remaining from error details
            let attemptsMsg = "";
            try {
              // The error message might contain details about attempts
              if (apiError.message.includes("attempts")) {
                attemptsMsg = " " + apiError.message;
              }
            } catch {
              // Ignore parsing errors
            }
            return {
              success: false,
              error: "驗證碼錯誤" + attemptsMsg,
            };
          }
          default:
            return {
              success: false,
              error: apiError.message || "驗證失敗",
            };
        }
      }

      return {
        success: false,
        error: error instanceof Error ? error.message : "驗證失敗",
      };
    }
  }

  /**
   * Get currently stored authentication tokens
   */
  static getStoredTokens(): AuthResponse | null {
    return getAuthTokens();
  }
}
